import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Text,
  HStack,
  VStack,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Alert,
  AlertIcon,
  Spinner,
  Center,
  useToast,
  Card,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  SimpleGrid,
  Divider,
  IconButton,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Code,
} from '@chakra-ui/react';
import { CheckIcon, CloseIcon, DeleteIcon, RepeatIcon } from '@chakra-ui/icons';
import { FiUsers, FiUserCheck, FiSettings, FiUserPlus, FiKey } from 'react-icons/fi';
import { adminApi, UserStatus } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

// Badge colorido por status do usuário
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const colorMap: Record<string, string> = {
    approved: 'green',
    pending: 'yellow',
    rejected: 'red',
  };
  const labelMap: Record<string, string> = {
    approved: 'Aprovado',
    pending: 'Pendente',
    rejected: 'Rejeitado',
  };
  return (
    <Badge colorScheme={colorMap[status] || 'gray'}>
      {labelMap[status] || status}
    </Badge>
  );
};

// Formata data ISO para exibição legível
const formatDate = (dateStr?: string) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const Admin: React.FC = () => {
  const { user } = useAuth();
  const toast = useToast();

  const [pendingUsers, setPendingUsers] = useState<UserStatus[]>([]);
  const [allUsers, setAllUsers] = useState<UserStatus[]>([]);
  const [loadingPending, setLoadingPending] = useState(true);
  const [loadingAll, setLoadingAll] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Adicionar usuário à empresa (convite de equipe) — hooks no topo (antes de returns).
  const addUser = useDisclosure();
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '' });
  const [creating, setCreating] = useState(false);
  const [createdPwd, setCreatedPwd] = useState<{ email: string; pwd: string } | null>(null);
  // Reset de senha de um membro (gera nova provisória)
  const [resetInfo, setResetInfo] = useState<{ email: string; pwd: string } | null>(null);

  // Todos os hooks devem estar antes de qualquer return condicional
  const fetchPending = useCallback(async () => {
    setLoadingPending(true);
    try {
      const data = await adminApi.getPendingUsers();
      setPendingUsers(data);
    } catch {
      toast({ title: 'Erro ao carregar pendentes', status: 'error', duration: 3000 });
    } finally {
      setLoadingPending(false);
    }
  }, [toast]);

  const fetchAll = useCallback(async () => {
    setLoadingAll(true);
    try {
      const data = await adminApi.getAllUsers();
      setAllUsers(data);
    } catch {
      toast({ title: 'Erro ao carregar usuários', status: 'error', duration: 3000 });
    } finally {
      setLoadingAll(false);
    }
  }, [toast]);

  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    fetchPending();
    fetchAll();
  }, [user, fetchPending, fetchAll]);

  // Redireciona se não for admin — após todos os hooks
  if (!user || user.role !== 'admin') {
    return (
      <Alert status="error">
        <AlertIcon />
        Acesso restrito a administradores.
      </Alert>
    );
  }

  const handleApprove = async (email: string) => {
    setActionLoading(email);
    try {
      await adminApi.approveUser(email);
      toast({ title: `${email} aprovado`, status: 'success', duration: 3000 });
      await Promise.all([fetchPending(), fetchAll()]);
    } catch {
      toast({ title: 'Erro ao aprovar usuário', status: 'error', duration: 3000 });
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (email: string) => {
    setActionLoading(email);
    try {
      await adminApi.rejectUser(email);
      toast({ title: `${email} rejeitado`, status: 'warning', duration: 3000 });
      await Promise.all([fetchPending(), fetchAll()]);
    } catch {
      toast({ title: 'Erro ao rejeitar usuário', status: 'error', duration: 3000 });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (email: string) => {
    if (!window.confirm(`Remover o usuário ${email}? Esta ação não pode ser desfeita.`)) return;
    setActionLoading(email);
    try {
      await adminApi.deleteUser(email);
      toast({ title: `${email} removido`, status: 'info', duration: 3000 });
      await fetchAll();
    } catch {
      toast({ title: 'Erro ao remover usuário', status: 'error', duration: 3000 });
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetPassword = async (email: string) => {
    if (!window.confirm(`Gerar uma nova senha provisória para ${email}? A senha atual deixará de funcionar.`)) return;
    setActionLoading(email);
    try {
      const r = await adminApi.resetPassword(email);
      setResetInfo({ email: r.email, pwd: r.temp_password });
    } catch (e: any) {
      toast({ title: 'Erro ao resetar senha', description: e?.response?.data?.detail, status: 'error', duration: 4000 });
    } finally {
      setActionLoading(null);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.name.trim() || !newUser.email.includes('@')) {
      toast({ title: 'Informe nome e e-mail válido', status: 'warning', duration: 3000 });
      return;
    }
    setCreating(true);
    try {
      const r = await adminApi.createUser({
        name: newUser.name.trim(),
        email: newUser.email.trim(),
        password: newUser.password.trim() || undefined,
      });
      // Se o convite foi por e-mail, não precisa mostrar a senha; senão, exibimos uma vez.
      if (r.temp_password) setCreatedPwd({ email: r.email, pwd: r.temp_password });
      else {
        toast({
          title: 'Usuário adicionado',
          description: r.emailed ? 'Enviamos o acesso por e-mail para o usuário.' : undefined,
          status: 'success', duration: 4000,
        });
        addUser.onClose();
      }
      setNewUser({ name: '', email: '', password: '' });
      await fetchAll();
    } catch (e: any) {
      toast({ title: 'Não foi possível adicionar', description: e?.response?.data?.detail || 'Tente novamente', status: 'error', duration: 5000 });
    } finally {
      setCreating(false);
    }
  };

  const approvedCount = allUsers.filter(u => u.status === 'approved').length;

  return (
    <VStack spacing={6} align="stretch">
      <HStack justify="space-between" align="center">
        <Heading size="lg" color="brand.600">
          Painel Administrativo
        </Heading>
        <HStack spacing={3}>
          <Button leftIcon={<FiUserPlus />} colorScheme="brand" size="sm" onClick={addUser.onOpen}>
            Adicionar usuário
          </Button>
          <Text color="gray.500" fontSize="sm">Logado como: {user.email}</Text>
        </HStack>
      </HStack>

      {/* Cards de resumo */}
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <Card>
          <CardBody>
            <Stat>
              <StatLabel color="gray.500">Aprovações pendentes</StatLabel>
              <StatNumber color={pendingUsers.length > 0 ? 'yellow.500' : 'gray.400'}>
                {loadingPending ? '...' : pendingUsers.length}
              </StatNumber>
            </Stat>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <Stat>
              <StatLabel color="gray.500">Usuários aprovados</StatLabel>
              <StatNumber color="green.500">
                {loadingAll ? '...' : approvedCount}
              </StatNumber>
            </Stat>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <Stat>
              <StatLabel color="gray.500">Total de cadastros</StatLabel>
              <StatNumber color="brand.600">
                {loadingAll ? '...' : allUsers.length}
              </StatNumber>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      <Divider />

      {/* Abas */}
      <Tabs colorScheme="brand" variant="enclosed">
        <TabList>
          <Tab>
            <HStack spacing={2}>
              <Box as={FiUserCheck} />
              <Text>Aprovações pendentes</Text>
              {pendingUsers.length > 0 && (
                <Badge colorScheme="yellow" borderRadius="full">
                  {pendingUsers.length}
                </Badge>
              )}
            </HStack>
          </Tab>
          <Tab>
            <HStack spacing={2}>
              <Box as={FiUsers} />
              <Text>Todos os usuários</Text>
            </HStack>
          </Tab>
        </TabList>

        <TabPanels>
          {/* Aba de pendentes */}
          <TabPanel px={0}>
            <HStack justify="flex-end" mb={3}>
              <Tooltip label="Atualizar lista">
                <IconButton
                  aria-label="Atualizar"
                  icon={<RepeatIcon />}
                  size="sm"
                  variant="ghost"
                  onClick={fetchPending}
                />
              </Tooltip>
            </HStack>

            {loadingPending ? (
              <Center py={8}>
                <Spinner color="brand.500" />
              </Center>
            ) : pendingUsers.length === 0 ? (
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                Nenhum cadastro aguardando aprovação.
              </Alert>
            ) : (
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th>Nome</Th>
                      <Th>Email</Th>
                      <Th>Data do cadastro</Th>
                      <Th>Ações</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {pendingUsers.map((u) => (
                      <Tr key={u.email}>
                        <Td fontWeight="medium">{u.name}</Td>
                        <Td color="gray.600">{u.email}</Td>
                        <Td color="gray.500" fontSize="xs">{formatDate(u.created_at)}</Td>
                        <Td>
                          <HStack spacing={2}>
                            <Tooltip label="Aprovar">
                              <IconButton
                                aria-label="Aprovar"
                                icon={<CheckIcon />}
                                colorScheme="green"
                                size="sm"
                                isLoading={actionLoading === u.email}
                                onClick={() => handleApprove(u.email)}
                              />
                            </Tooltip>
                            <Tooltip label="Rejeitar">
                              <IconButton
                                aria-label="Rejeitar"
                                icon={<CloseIcon />}
                                colorScheme="red"
                                size="sm"
                                isLoading={actionLoading === u.email}
                                onClick={() => handleReject(u.email)}
                              />
                            </Tooltip>
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </TabPanel>

          {/* Aba todos os usuários */}
          <TabPanel px={0}>
            <HStack justify="flex-end" mb={3}>
              <Tooltip label="Atualizar lista">
                <IconButton
                  aria-label="Atualizar"
                  icon={<RepeatIcon />}
                  size="sm"
                  variant="ghost"
                  onClick={fetchAll}
                />
              </Tooltip>
            </HStack>

            {loadingAll ? (
              <Center py={8}>
                <Spinner color="brand.500" />
              </Center>
            ) : (
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th>Nome</Th>
                      <Th>Email</Th>
                      <Th>Perfil</Th>
                      <Th>Status</Th>
                      <Th>Cadastro</Th>
                      <Th>Aprovado em</Th>
                      <Th>Ações</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {allUsers.map((u) => (
                      <Tr key={u.email}>
                        <Td fontWeight="medium">{u.name}</Td>
                        <Td color="gray.600">{u.email}</Td>
                        <Td>
                          <Badge colorScheme={u.role === 'admin' ? 'purple' : 'blue'}>
                            {u.role === 'admin' ? 'Admin' : 'Usuário'}
                          </Badge>
                        </Td>
                        <Td><StatusBadge status={u.status} /></Td>
                        <Td color="gray.500" fontSize="xs">{formatDate(u.created_at)}</Td>
                        <Td color="gray.500" fontSize="xs">{formatDate(u.approved_at)}</Td>
                        <Td>
                          <HStack spacing={1}>
                            {/* Reset de senha (qualquer membro, menos você mesmo) */}
                            {u.email !== user.email && (
                              <Tooltip label="Resetar senha">
                                <IconButton
                                  aria-label="Resetar senha"
                                  icon={<FiKey />}
                                  colorScheme="orange"
                                  size="sm"
                                  variant="ghost"
                                  isLoading={actionLoading === u.email}
                                  onClick={() => handleResetPassword(u.email)}
                                />
                              </Tooltip>
                            )}
                            {/* Não permite deletar admins */}
                            {u.role !== 'admin' && (
                              <Tooltip label="Remover usuário">
                                <IconButton
                                  aria-label="Remover"
                                  icon={<DeleteIcon />}
                                  colorScheme="red"
                                  size="sm"
                                  variant="ghost"
                                  isLoading={actionLoading === u.email}
                                  onClick={() => handleDelete(u.email)}
                                />
                              </Tooltip>
                            )}
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Modal: adicionar usuário à empresa */}
      <Modal isOpen={addUser.isOpen} onClose={() => { addUser.onClose(); setCreatedPwd(null); }} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Adicionar usuário à equipe</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {createdPwd ? (
              <VStack align="stretch" spacing={3}>
                <Alert status="success" borderRadius="md"><AlertIcon />Usuário criado!</Alert>
                <Text fontSize="sm">
                  Repasse estes dados para <b>{createdPwd.email}</b>. A senha provisória aparece só agora:
                </Text>
                <Box bg="gray.50" borderWidth="1px" borderRadius="md" p={3}>
                  <Text fontSize="sm">E-mail: <Code>{createdPwd.email}</Code></Text>
                  <Text fontSize="sm" mt={1}>Senha provisória: <Code>{createdPwd.pwd}</Code></Text>
                </Box>
                <Text fontSize="xs" color="gray.500">Peça para a pessoa entrar e trocar a senha depois.</Text>
              </VStack>
            ) : (
              <VStack align="stretch" spacing={4}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Nome</FormLabel>
                  <Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} placeholder="Nome do colaborador" />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">E-mail</FormLabel>
                  <Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} placeholder="email@empresa.com" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Senha (opcional)</FormLabel>
                  <Input type="text" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} placeholder="Deixe em branco para gerar automaticamente" />
                </FormControl>
                <Text fontSize="xs" color="gray.500">
                  O usuário já entra ativo na sua empresa. Se deixar a senha em branco, geramos uma provisória para você repassar.
                </Text>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            {createdPwd ? (
              <Button colorScheme="brand" onClick={() => { addUser.onClose(); setCreatedPwd(null); }}>Concluir</Button>
            ) : (
              <Button colorScheme="brand" onClick={handleCreateUser} isLoading={creating}>Adicionar</Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal: senha provisória após reset */}
      <Modal isOpen={!!resetInfo} onClose={() => setResetInfo(null)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Senha resetada</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <Alert status="success" borderRadius="md"><AlertIcon />Nova senha gerada!</Alert>
              <Text fontSize="sm">Repasse para <b>{resetInfo?.email}</b> — a senha antiga não funciona mais:</Text>
              <Box bg="gray.50" borderWidth="1px" borderRadius="md" p={3}>
                <Text fontSize="sm">E-mail: <Code>{resetInfo?.email}</Code></Text>
                <Text fontSize="sm" mt={1}>Nova senha: <Code>{resetInfo?.pwd}</Code></Text>
              </Box>
              <Text fontSize="xs" color="gray.500">Peça para a pessoa entrar e trocar a senha no menu do usuário.</Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="brand" onClick={() => setResetInfo(null)}>Concluir</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default Admin;
